import { getUserMerchants } from '../api/merchantApi';
import { filterActiveMerchants, type Merchant } from '../types/merchant';

/**
 * Get active merchants from login response first; if empty, fallback to merchant API.
 * Returns { merchants, error } so callers can show API error (e.g. 403) when fallback fails.
 *
 * The fallback API call includes automatic retry logic (3 attempts).
 * User authentication is handled automatically via Authorization token.
 * All merchants are filtered to return only active ones (merchant_status === 'active').
 *
 * @param loginMerchants - Merchants array from login response (member_details.merchants)
 * @returns Object with active merchants array and optional error
 */
export async function getMerchantsWithLoginFallback(
  loginMerchants: Merchant[] | undefined | null,
): Promise<{ merchants: Merchant[]; error?: unknown }> {
  // Filter to active merchants from login response
  const fromLogin = filterActiveMerchants(loginMerchants);
  if (fromLogin.length > 0) return { merchants: fromLogin };

  // Fallback: call merchant API (includes retry logic)
  // User is identified via Authorization token in headers
  try {
    const allMerchants = await getUserMerchants();
    // Filter to active merchants at the consumer level
    const merchants = filterActiveMerchants(allMerchants);
    return { merchants };
  } catch (e) {
    return { merchants: [], error: e };
  }
}
